import React, { useEffect, useState } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import "../../style/myStyle.css"; // Importa il tuo file CSS
import { useNavigate } from "react-router-dom";
import renderNotification from "../../utils/notification-handler";
import { ReactNotifications } from "react-notifications-component";

import { startToast, updateToastSuccess, updateToastError } from '../../utils/notification-loading';

import API from "../../api/index";

const ResetPassword = () => {
  const [code, setCode] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [psw, setPsw] = useState("");
  const [cpsw, setCpsw] = useState("");
  const queryParams = new URLSearchParams(window.location.search);
  const type = queryParams.get("type");
  const token = queryParams.get('token');
  const id = queryParams.get('id');
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    // Verifica che token, id e type siano presenti
    if (!token || !id || !type) {
      setIsTokenValid(false);
      setIsLoading(false);
      renderNotification(
        "error",
        "Error",
        "Invalid or missing reset link parameters"
      );
      setTimeout(() => {
        navigate("/");
      }, 3000);
      return;
    }

    // Verifica la validità del token con il server
    API.getPasswordResetPage({type:type,id:id,token:token})
      .then((response) => {
        setIsLoading(false);
        if (response && response.data === "Ok") {
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
          renderNotification(
            "error",
            "Error",
            "Invalid or expired reset token"
          );
          setTimeout(() => {
            navigate("/");
          }, 3000);
        }
      })
      .catch((err) => {
        console.error("[ResetPassword] Error validating token:", err);
        setIsLoading(false);
        setIsTokenValid(false);
        renderNotification(
          "error",
          "Error",
          err.response?.status === 401 ? "Invalid or expired reset token" : "Error validating reset link"
        );
        setTimeout(() => {
          navigate("/");
        }, 3000);
      });
  }, [token, id, type, navigate]);

  const handleCpswChange = (e) => {
    const confirmPsw = e.target.value;
    setCpsw(confirmPsw);
    if (psw !== confirmPsw) {
      setErrorMessage("Passwords do not match");
    } else {
      setErrorMessage("");
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };


  const handleSubmit = (event) => {
    event.preventDefault();
    
    // Validazione password prima di inviare
    if (passwordError) {
      renderNotification(
        "error",
        "Error",
        passwordError
      );
      return;
    }
    
    if (psw !== cpsw) {
      renderNotification(
        "error",
        "Error",
        "Passwords do not match"
      );
      return;
    }
    
    if (!code || code.length !== 6) {
      renderNotification(
        "error",
        "Error",
        "Please enter a valid 6-digit code"
      );
      return;
    }
    
    startToast();
    API.resetPassword({type:type,id:id,token:token,code:code, psw:psw })
      .then((response) => {
        updateToastSuccess("Password Reset Correctly");
        API.logout({})
        .then(() => {
          window.sessionStorage.clear()
          navigate("/");
        })
        .catch(() => {
          window.sessionStorage.clear()
          navigate("/");
        });
        setTimeout(() => {
          window.location.replace('/')
        }, 1500);
      })
      .catch((err) => {
        console.error("[handleSubmit] Reset password error:", err);
        let errorMessage = "Error resetting password";
        
        if (err.response) {
          // Errore dal server
          if (err.response.status === 403) {
            errorMessage = "Invalid or expired reset code. Please request a new password reset.";
          } else if (err.response.status === 400) {
            errorMessage = "Invalid request. Please check all fields.";
          } else if (err.response.status === 500) {
            errorMessage = "Server error. Please try again later.";
          } else if (err.response.data && err.response.data.err) {
            errorMessage = err.response.data.err;
          }
        } else if (err.request) {
          errorMessage = "Network error. Please check your connection.";
        }
        
        setSubmitError(errorMessage);
        updateToastError(errorMessage);
        renderNotification(
          "error",
          "Error",
          errorMessage
        );
      });
  };

  const handlePswChange = (e) => {
    const newPsw = e.target.value;
    setPsw(newPsw);
    const errorMessage = validatePassword(newPsw);
    setPasswordError(errorMessage);
  };

  const validatePassword = (password) => {
    const lengthRegex = /^.{8,24}$/; // Lunghezza tra 8 e 24 caratteri
    const uppercaseRegex = /[A-Z]/; // Almeno una lettera maiuscola
    const numberRegex = /[0-9]/; // Almeno un numero
    const specialCharRegex = /[^A-Za-z0-9]/; // Almeno un carattere speciale

    if (!lengthRegex.test(password)) {
      return "The password must be between 8 and 24 characters long.";
    }
    if (!uppercaseRegex.test(password)) {
      return "The password must contain at least one capital letter.";
    }
    if (!numberRegex.test(password)) {
      return "The password must contain at least one number.";
    }
    if (!specialCharRegex.test(password)) {
      return "The password must contain at least one special character.";
    }
    return "";
  };

  // Mostra sempre qualcosa - fallback per evitare pagina bianca
  // Gestione stati di caricamento e validità token
  if (isLoading) {
    return (
      <>
        <div className="app-container" style={{justifyContent: 'center', display: 'flex', alignItems: 'center', minHeight: '100vh'}}>
          <ReactNotifications />
          <Container component="main" maxWidth="xs">
            <Typography component="h1" variant="h5" style={{color: '#fff', textAlign: 'center'}}>
              Verifying reset link...
            </Typography>
          </Container>
        </div>
      </>
    );
  }

  // Se il token non è valido, mostra messaggio di errore
  if (!isTokenValid) {
    return (
      <>
        <div className="app-container" style={{justifyContent: 'center', display: 'flex', alignItems: 'center', minHeight: '100vh'}}>
          <ReactNotifications />
          <Container component="main" maxWidth="xs" sx={{
            backgroundColor: "#1e1e1e !important",
            borderRadius: "15px",
            border: "solid 3px #ff4444",
            marginTop: 8,
            padding: 3
          }}>
            <Typography component="h1" variant="h5" style={{color: '#ff4444', textAlign: 'center', marginBottom: 2}}>
              Invalid or Expired Link
            </Typography>
            <Typography component="p" variant="body1" style={{color: '#fff', textAlign: 'center'}}>
              This password reset link is invalid or has expired. Please request a new one.
            </Typography>
          </Container>
        </div>
      </>
    );
  }

  // Se il token è valido, mostra il form (questo è il caso normale)
  // Assicuriamoci che token, id e type siano ancora presenti
  if (!token || !id || !type) {
    return (
      <>
        <div className="app-container" style={{justifyContent: 'center', display: 'flex', alignItems: 'center', minHeight: '100vh'}}>
          <ReactNotifications />
          <Container component="main" maxWidth="xs" sx={{
            backgroundColor: "#1e1e1e !important",
            borderRadius: "15px",
            border: "solid 3px #ff4444",
            marginTop: 8,
            padding: 3
          }}>
            <Typography component="h1" variant="h5" style={{color: '#ff4444', textAlign: 'center', marginBottom: 2}}>
              Missing Parameters
            </Typography>
            <Typography component="p" variant="body1" style={{color: '#fff', textAlign: 'center'}}>
              Required parameters are missing from the URL.
            </Typography>
          </Container>
        </div>
      </>
    );
  }

  return (
  <>
    <div className="app-container" style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
      <ReactNotifications />
      <Container component="main" maxWidth="xs" sx={{
        backgroundColor: "#1e1e1e !important",
        borderRadius: "15px",
        border: "solid 3px #449095",
        marginTop: 8
        }}>
        <Box
          sx={{
            marginTop: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography component="h1" variant="h5">
            Enter the code
          </Typography>
          <Typography component="h7" variant="h7">
            The code can be found at the email entered
          </Typography>
          {submitError && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#ff4444', borderRadius: 1, width: '100%' }}>
              <Typography variant="body2" style={{color: '#fff', textAlign: 'center'}}>
                {submitError}
              </Typography>
            </Box>
          )}
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="code"
              label="Enter the code"
              name="code"
              autoComplete="code"
              autoFocus
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
            />
            <TextField
              required
              label="Password"
              type={showConfirmPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              onChange={handlePswChange}
              error={!!passwordError}
              helperText={passwordError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle password visibility"
                      onClick={handleToggleConfirmPassword}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              required
              label="Confirm Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              onChange={handleCpswChange}
              error={!!errorMessage}
              helperText={errorMessage}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle password visibility"
                      onClick={handleTogglePassword}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ 
                mt: 3, 
                mb: 2, 
                backgroundColor: "#2d2d2d",
                color: "#fff",
                "&:hover" : {
                  backgroundColor: "eaeaea",
                  color: "#2d2d2d"
                }}
              }
            >
              Send
            </Button>
          </Box>
        </Box>
      </Container>
    </div>
    </>

  );
};

export default ResetPassword;
